import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileSpreadsheet, ChevronDown, ChevronUp } from "lucide-react";

/** Admin-only Excel invoice-template manager.
 *  Handles upload/download/delete for the India + LLC templates and shows
 *  the placeholder-token reference so the admin knows what to embed. */
const ENTITY_LABEL = { india: "Indian Invoice Template", llc: "LLC Invoice Template" };

function EntityBlock({ entity, meta, tokens, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isStarter = meta?.is_starter;

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) return toast.error("Please upload a .xlsx file");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/api/settings/invoice-templates/${entity}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${ENTITY_LABEL[entity]} uploaded — new invoices will use it`);
      onUploaded && onUploaded();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadStarter = async () => {
    try {
      const res = await api.get(`/api/settings/invoice-templates/${entity}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity}_invoice_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not download template"));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Reset the ${ENTITY_LABEL[entity]} to the built-in starter?`)) return;
    try {
      await api.delete(`/api/settings/invoice-templates/${entity}`);
      toast.success("Template reset to starter");
      onUploaded && onUploaded();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not reset template"));
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-brand" />
            <div className="font-heading font-semibold text-sm text-slate-800">{ENTITY_LABEL[entity]}</div>
            {isStarter && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Starter</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {meta?.filename || "No template — uploading Excel will overwrite the default"}
            {meta?.uploaded_at && !isStarter && (
              <> · uploaded {new Date(meta.uploaded_at).toLocaleString()}
                {meta.uploaded_by ? ` by ${meta.uploaded_by}` : ""}</>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input type="file" ref={fileRef} accept=".xlsx" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`tpl-${entity}-file`} />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid={`tpl-${entity}-upload`}>
            <Upload className="w-3.5 h-3.5 mr-1.5" />{uploading ? "Uploading…" : "Upload .xlsx"}
          </Button>
          <Button size="sm" variant="outline" onClick={downloadStarter} data-testid={`tpl-${entity}-download`}>
            <Download className="w-3.5 h-3.5 mr-1.5" />Download {isStarter ? "starter" : "current"}
          </Button>
          {!isStarter && (
            <Button size="sm" variant="outline" onClick={remove} className="text-rose-600 border-rose-200 hover:bg-rose-50"
              data-testid={`tpl-${entity}-delete`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function InvoiceTemplatesSection({ tokens }) {
  const [meta, setMeta] = useState(null);
  const [showTokens, setShowTokens] = useState(false);

  const reload = () => {
    api.get("/api/settings/invoice-templates").then((r) => setMeta(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Could not load templates")));
  };
  useEffect(() => { reload(); }, []);

  if (!meta) return null;

  return (
    <Card className="border-slate-200 shadow-none p-6" data-testid="invoice-templates-card">
      <div className="font-heading font-medium mb-1">Invoice Templates (Excel → PDF)</div>
      <div className="text-sm text-slate-500 mb-5">
        Admin uploads an <b>.xlsx</b> template per entity. On download, we substitute
        placeholders like <code className="bg-slate-100 px-1 rounded">{'{{invoice_number}}'}</code>
        &nbsp;and expand the line-items loop, then convert to a single-page A4 PDF via LibreOffice —
        so the PDF is byte-for-byte a copy of your Excel layout.
      </div>

      <div className="space-y-3">
        <EntityBlock entity="india" meta={meta.india} onUploaded={reload} />
        <EntityBlock entity="llc" meta={meta.llc} onUploaded={reload} />
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4">
        <button
          className="text-sm text-brand hover:underline inline-flex items-center gap-1"
          onClick={() => setShowTokens((v) => !v)}
          data-testid="toggle-token-reference"
        >
          {showTokens ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showTokens ? "Hide" : "Show"} placeholder token reference
        </button>
        {showTokens && tokens && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            {Object.entries(tokens).map(([group, val]) => (
              <div key={group} className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  {group.replace(/_/g, " ")}
                </div>
                {Array.isArray(val) ? (
                  <div className="flex flex-wrap gap-1.5">
                    {val.map((tok) => (
                      <code key={tok} className="bg-white border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                        {`{{${tok}}}`}
                      </code>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div><span className="text-slate-500">Open:</span> <code>{val.open}</code></div>
                    <div><span className="text-slate-500">Close:</span> <code>{val.close}</code></div>
                    <div><span className="text-slate-500">Per-row:</span></div>
                    <div className="flex flex-wrap gap-1.5">
                      {val.per_row_tokens.map((tok) => (
                        <code key={tok} className="bg-white border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                          {`{{${tok}}}`}
                        </code>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
